import {
   Component,
   EventEmitter,
   Input,
   OnDestroy,
   OnInit,
   Output
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { RowButton } from '../models/row-button';
import { Group } from '../../domain/group';
import { GroupStoreService } from 'src/app/services/stores/group-store.service';
import { UserPermissions } from '../../domain/security/user-permissions';
import { Property } from '../../domain/property';
import { PropertyStoreService } from '../../services/stores/property-store.service';
import { UpdateGroupRequest } from '../../services/request/update-group-request';
import { DataStoreService } from '../../services/data-store.service';
import { MessageService } from 'primeng/api';
import { PermissionsStoreService } from '../../services/stores/permission-store-service';

@Component({
   selector: 'app-groups-table',
   templateUrl: './groups-table.component.html',
   styleUrls: ['./groups-table.component.scss'],
   providers: [GroupStoreService, MessageService]
})
export class GroupsTableComponent implements OnInit, OnDestroy {
   headingButtons: RowButton[];

   @Input() groups: Group[] = [];

   isCurrentlyVisible: boolean;
   userPermissions: UserPermissions;
   currentProperty: Property | undefined;

   @Output() addGroupsDialogOpen = new EventEmitter();

   isLoading = true;

   private destroy$ = new Subject<void>();

   constructor(
      private groupStore: GroupStoreService,
      private propertyStore: PropertyStoreService,
      private dataStoreService: DataStoreService,
      private messageService: MessageService,
      private permissionsStore: PermissionsStoreService
   ) {
      this.isCurrentlyVisible = true;

      this.headingButtons = [
         new RowButton(
            'add_group',
            'Create Group',
            'fal fa-plus',
            [
               'p-button-rounded',
               'p-button-success',
               'p-button-outlined',
               'p-mr-2',
               'p-mb-2'
            ],
            ['p-col-12', 'p-text-right'],
            'right'
         )
      ];

      this.userPermissions = new UserPermissions();
   }

   ngOnInit(): void {
      // Permissions (store)
      this.permissionsStore
         .get()
         .pipe(takeUntil(this.destroy$))
         .subscribe((p) => {
            this.userPermissions = p ?? new UserPermissions();

            // keep button visibility in sync with permissions
            const canCreate = !!this.userPermissions.user?.create;
            this.headingButtons.forEach((x) => (x.isVisible = canCreate));
         });

      // Property (unchanged behaviour, but now cleaned up)
      this.propertyStore.currentProperty
         .pipe(takeUntil(this.destroy$))
         .subscribe((property: any) => {
            this.currentProperty = property;
         });

      // Groups from store
      this.groupStore.groups.pipe(takeUntil(this.destroy$)).subscribe((groups) => {
         this.groups = groups;
         this.isLoading = false;
      });

      // Refresh trigger
      this.dataStoreService.refreshTaskDataSubject
         .pipe(takeUntil(this.destroy$))
         .subscribe((state) => {
            if (state.Groups) {
               this.isLoading = true;
               this.groupStore.refreshStore();
            }
         });

      // initial load
      this.isLoading = true;
      this.groupStore.refreshStore();
   }

   ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
   }

   onButtonRowClick(id: string) {
      switch (id) {
         case 'add_group':
            this.addGroupsDialogOpen.emit();
            break;
         default:
            break;
      }
   }

   updateGroup(group: Group) {
      const request = new UpdateGroupRequest();
      request.isActive = !group.isActive;

      this.groupStore.updateGroup(group, request).subscribe({
         next: () => {
            this.groupStore.refreshStore();
            this.messageService.add({
               severity: 'success',
               summary: 'Group Updated',
               detail: `The group "${group.displayName}" has been successfully ${
                  request.isActive ? 'activated' : 'deactivated'
               }.`,
               life: 3000
            });
         },
         error: (error) => {
            console.error(error);
            this.messageService.add({
               severity: 'error',
               summary: 'Error Updating Group',
               detail: 'There was an issue activating this the group.',
               life: 5000
            });
         }
      });
   }
}
